import { gsap, ScrollTrigger } from '../gsap-config.js';
import { initAnimationsForElement, cleanupAnimationsForElement } from '../utils/animations.js';
import { prefersReducedMotion } from '../utils/media.js';

const REPEAT_DELAY = 1.8;

function q(root, selector) {
	return root.querySelector(selector);
}

function qa(root, selector) {
	return Array.from(root.querySelectorAll(selector));
}

function drawLine(path, duration = 0.6) {
	if (!path) return gsap.timeline();
	const length = path.getTotalLength?.() || 48;
	gsap.set(path, { strokeDasharray: length, strokeDashoffset: length, opacity: 1 });
	return gsap.to(path, { strokeDashoffset: 0, duration, ease: 'power2.out' });
}

function playWhenVisible(tile, buildTimeline) {
	const svg = q(tile, '[data-bento-animation]');
	if (!svg) return null;

	const timeline = buildTimeline(svg, tile);
	if (!timeline) return null;

	timeline.pause(0);

	const trigger = ScrollTrigger.create({
		trigger: tile,
		start: 'top 82%',
		end: 'bottom 15%',
		onEnter: () => {
			tile.classList.add('is-svg-active');
			timeline.restart(true);
		},
		onEnterBack: () => {
			tile.classList.add('is-svg-active');
			timeline.restart(true);
		},
		onLeave: () => {
			timeline.pause(0);
			tile.classList.remove('is-svg-active');
		},
		onLeaveBack: () => {
			timeline.pause(0);
			tile.classList.remove('is-svg-active');
		},
	});

	return { timeline, trigger };
}

function buildConversionTimeline(svg) {
	const controlLayers = qa(svg, '[data-bento-layer="control"]');
	const variantLayers = qa(svg, '[data-bento-layer="variant"]');

	gsap.set(controlLayers, { opacity: 1 });
	gsap.set(variantLayers, { opacity: 0 });
	gsap.set(q(svg, '.bento-cro__variant-badge'), { y: 6, opacity: 0 });

	const tl = gsap.timeline({ repeat: -1, repeatDelay: REPEAT_DELAY });

	tl.to(controlLayers, { opacity: 0, duration: 0.45, stagger: 0.04, ease: 'power2.inOut' })
		.to(variantLayers, { opacity: 1, duration: 0.55, stagger: 0.05, ease: 'power2.out' }, '-=0.25')
		.fromTo(q(svg, '.bento-cro__variant-cta'), { scale: 0.92, transformOrigin: '10px 71px' }, { scale: 1, duration: 0.35, ease: 'back.out(2)' }, '-=0.35')
		.to(q(svg, '.bento-cro__variant-badge'), { opacity: 1, y: 0, duration: 0.35, ease: 'power2.out' }, '-=0.1')
		.to({}, { duration: 1.2 })
		.to(variantLayers, { opacity: 0, duration: 0.4, stagger: 0.03, ease: 'power2.inOut' })
		.to(controlLayers, { opacity: 1, duration: 0.45, stagger: 0.04, ease: 'power2.out' }, '-=0.2')
		.set(q(svg, '.bento-cro__variant-badge'), { y: 6, opacity: 0 });

	return tl;
}

function buildThemeTimeline(svg) {
	const sections = qa(svg, '.bento-theme__section');
	gsap.set(sections, { opacity: 0.4, scale: 1, transformOrigin: 'center center' });

	const tl = gsap.timeline({ repeat: -1, repeatDelay: REPEAT_DELAY });

	sections.forEach((section) => {
		tl.to(sections, { opacity: 0.35, duration: 0.2 })
			.to(section, { opacity: 1, scale: 1.03, duration: 0.35, ease: 'power2.out' })
			.to(section, { scale: 1, duration: 0.2 });
	});

	return tl;
}

function buildAppsTimeline(svg) {
	const tiles = qa(svg, '.bento-apps__tile');
	const strike = qa(svg, '[data-bento-layer="strike"]');

	gsap.set(tiles, { scale: 1, transformOrigin: 'center center' });
	gsap.set(strike, { opacity: 0 });

	const tl = gsap.timeline({ repeat: -1, repeatDelay: REPEAT_DELAY });

	tiles.forEach((tile, index) => {
		tl.to(tile, { scale: 1.04, duration: 0.3, ease: 'power2.out' }, index * 0.2)
			.to(tile, { scale: 1, duration: 0.25, ease: 'power2.inOut' });
	});

	tl.to(q(svg, '[data-bento-layer="app-3"]'), { scale: 1.06, duration: 0.35, ease: 'back.out(1.6)' })
		.to(strike, { opacity: 1, duration: 0.35 }, '-=0.1')
		.to({}, { duration: 0.8 })
		.to(strike, { opacity: 0, duration: 0.25 })
		.to(q(svg, '[data-bento-layer="app-3"]'), { scale: 1, duration: 0.25 });

	return tl;
}

function buildDataTimeline(svg) {
	const lines = qa(svg, '.bento-data__line');
	const pulse = q(svg, '.bento-data__pulse');

	lines.forEach((line) => {
		const length = line.getTotalLength();
		gsap.set(line, { strokeDasharray: length, strokeDashoffset: length, opacity: 0.25 });
	});

	const tl = gsap.timeline({ repeat: -1, repeatDelay: REPEAT_DELAY });

	tl.to(q(svg, '[data-bento-layer="node-center"]'), { scale: 1.04, transformOrigin: '150px 30px', duration: 0.35, yoyo: true, repeat: 1, ease: 'power2.inOut' });

	lines.forEach((line, index) => {
		tl.to(line, { strokeDashoffset: 0, opacity: 1, duration: 0.45, ease: 'power2.out' }, index * 0.15);
		tl.to(q(svg, `[data-bento-layer="node-${index + 1}"]`), { opacity: 1, scale: 1.03, transformOrigin: 'center', duration: 0.25 }, `-=0.2`);
	});

	tl.to(pulse, { opacity: 0.9, scale: 1.8, transformOrigin: 'center', duration: 0.35, ease: 'power2.out' })
		.to(pulse, { opacity: 0, scale: 1, duration: 0.35 });

	return tl;
}

function buildIntegrationTimeline(svg) {
	const leftPacket = q(svg, '[data-bento-layer="packet-left"]');
	const rightPacket = q(svg, '[data-bento-layer="packet-right"]');

	gsap.set([leftPacket, rightPacket], { opacity: 0 });

	const tl = gsap.timeline({ repeat: -1, repeatDelay: REPEAT_DELAY });

	tl.to(q(svg, '[data-bento-layer="hub"]'), { scale: 1.03, transformOrigin: '150px 95px', duration: 0.35, ease: 'power2.out' })
		.to(leftPacket, { opacity: 1, duration: 0.1 })
		.to(leftPacket, { x: 30, duration: 0.7, ease: 'power2.inOut' })
		.to(leftPacket, { opacity: 0, duration: 0.1 })
		.set(leftPacket, { x: 0 })
		.to(rightPacket, { opacity: 1, duration: 0.1 })
		.to(rightPacket, { x: -30, duration: 0.7, ease: 'power2.inOut' })
		.to(rightPacket, { opacity: 0, duration: 0.1 })
		.set(rightPacket, { x: 0 })
		.to(q(svg, '[data-bento-layer="hub"]'), { scale: 1, duration: 0.3 });

	return tl;
}

function buildCheckoutTimeline(svg) {
	const steps = ['cart', 'shipping', 'payment'];
	const tl = gsap.timeline({ repeat: -1, repeatDelay: REPEAT_DELAY });

	steps.forEach((step) => {
		const el = q(svg, `[data-bento-layer="${step}"]`);
		tl.to(el, { opacity: 1, scale: 1.02, transformOrigin: 'center', duration: 0.35, ease: 'power2.out' })
			.to(el, { opacity: 0.55, scale: 1, duration: 0.25 });
	});

	tl.fromTo(q(svg, '[data-bento-layer="extension"]'), { opacity: 0, x: 8 }, { opacity: 1, x: 0, duration: 0.45, ease: 'power2.out' })
		.to(q(svg, '[data-bento-layer="payment"] rect:last-of-type'), { scale: 1.04, transformOrigin: 'center', duration: 0.25, yoyo: true, repeat: 1 });

	return tl;
}

function buildPerformanceTimeline(svg) {
	const bars = qa(svg, '.bento-perf__bar rect');
	gsap.set(bars, { scaleY: 0, transformOrigin: 'bottom center' });

	const tl = gsap.timeline({ repeat: -1, repeatDelay: REPEAT_DELAY });

	tl.to(bars, { scaleY: 1, duration: 0.55, stagger: 0.08, ease: 'power2.out' })
		.fromTo(q(svg, '.bento-perf__badge'), { scale: 0.6, opacity: 0, transformOrigin: 'center' }, { scale: 1, opacity: 1, duration: 0.35, ease: 'back.out(2)' }, '-=0.2')
		.to({}, { duration: 0.9 })
		.to(bars, { scaleY: 0, duration: 0.35, stagger: 0.04, ease: 'power2.in' })
		.to(q(svg, '.bento-perf__badge'), { opacity: 0, duration: 0.2 }, '<');

	return tl;
}

function buildAdminTimeline(svg) {
	gsap.set(q(svg, '[data-bento-layer="nav-active"]'), { opacity: 0, y: 6 });
	gsap.set(q(svg, '[data-bento-layer="panel"]'), { opacity: 0.45, y: 8 });

	const tl = gsap.timeline({ repeat: -1, repeatDelay: REPEAT_DELAY });

	tl.fromTo(q(svg, '[data-bento-layer="nav-active"]'), { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.35, ease: 'power2.out' })
		.fromTo(q(svg, '[data-bento-layer="panel"]'), { opacity: 0.4, y: 8 }, { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }, '-=0.15')
		.to(q(svg, '[data-bento-layer="panel"] rect:last-of-type'), { opacity: 1, scale: 1.03, transformOrigin: 'center', duration: 0.3, yoyo: true, repeat: 1 });

	return tl;
}

function buildCodeTimeline(svg) {
	const lines = qa(svg, '.bento-code__line');
	const cursor = q(svg, '.bento-code__cursor');

	gsap.set(lines, { opacity: 0.35 });
	gsap.set(cursor, { opacity: 0 });

	const tl = gsap.timeline({ repeat: -1, repeatDelay: REPEAT_DELAY });

	lines.forEach((line, index) => {
		tl.to(line, { opacity: 1, duration: 0.25 }, index * 0.12);
		if (index === 2) {
			tl.to(cursor, { opacity: 1, x: 170, duration: 0.15 }, '-=0.05')
				.to(cursor, { opacity: 0, duration: 0.1, repeat: 3, yoyo: true, repeatDelay: 0.08 });
		}
	});

	return tl;
}

function buildFunctionsTimeline(svg) {
	const rules = qa(svg, '.bento-fn__rule');
	gsap.set(rules, { opacity: 0.5 });

	const tl = gsap.timeline({ repeat: -1, repeatDelay: REPEAT_DELAY });

	tl.to(q(svg, '[data-bento-layer="rule-2"]'), { opacity: 1, scale: 1.02, transformOrigin: 'center', duration: 0.35, ease: 'power2.out' })
		.to(q(svg, '[data-bento-layer="rule-2"]'), { scale: 1, duration: 0.25 })
		.to(q(svg, '[data-bento-layer="rule-2"]'), { opacity: 0.55, duration: 0.25 });

	return tl;
}

function buildMigrationTimeline(svg) {
	const dot = q(svg, '.bento-mig__dot');
	gsap.set(dot, { x: 0, opacity: 1 });

	const tl = gsap.timeline({ repeat: -1, repeatDelay: REPEAT_DELAY });

	tl.to(dot, { x: 66, duration: 0.8, ease: 'power2.inOut' })
		.to(q(svg, '[data-bento-layer="shopify"]'), { scale: 1.04, transformOrigin: '238px 95px', duration: 0.3, ease: 'back.out(1.8)' }, '-=0.15')
		.fromTo(q(svg, '[data-bento-layer="checklist"]'), { opacity: 0, y: 8 }, { opacity: 1, y: 0, duration: 0.35, ease: 'power2.out' })
		.to(q(svg, '[data-bento-layer="shopify"]'), { scale: 1, duration: 0.25 })
		.set(dot, { x: 0 })
		.to(q(svg, '[data-bento-layer="checklist"]'), { opacity: 0, duration: 0.2 });

	return tl;
}

function buildSeoTimeline(svg) {
	const tl = gsap.timeline({ repeat: -1, repeatDelay: REPEAT_DELAY });

	tl.fromTo(q(svg, '[data-bento-layer="result-1"]'), { y: 18, opacity: 0.4 }, { y: 0, opacity: 1, duration: 0.45, ease: 'power2.out' })
		.to(q(svg, '[data-bento-layer="arrow"]'), { opacity: 1, duration: 0.25 }, '-=0.1')
		.to(q(svg, '[data-bento-layer="result-2"]'), { y: 6, opacity: 0.7, duration: 0.3 }, '-=0.2')
		.to(q(svg, '[data-bento-layer="result-3"]'), { y: 10, opacity: 0.55, duration: 0.3 }, '-=0.25');

	return tl;
}

function buildOpsTimeline(svg) {
	const graphLine = q(svg, '.bento-ops__graph-line');
	const graphDot = q(svg, '.bento-ops__graph circle');

	if (graphLine) {
		const length = graphLine.getTotalLength();
		gsap.set(graphLine, { strokeDasharray: length, strokeDashoffset: length, opacity: 1 });
	}

	const tl = gsap.timeline({ repeat: -1, repeatDelay: REPEAT_DELAY });

	if (graphLine) {
		tl.to(graphLine, { strokeDashoffset: 0, duration: 0.9, ease: 'power2.out' })
			.to(graphDot, { opacity: 1, duration: 0.2 }, '-=0.15');
	}

	tl.fromTo(q(svg, '[data-bento-layer="alert"]'), { opacity: 0, scale: 0.92, transformOrigin: 'center' }, { opacity: 1, scale: 1, duration: 0.35, ease: 'back.out(2)' }, '-=0.1');

	return tl;
}

function buildBuyTimeline(svg) {
	const tl = gsap.timeline({ repeat: -1, repeatDelay: REPEAT_DELAY });

	tl.to(q(svg, '.bento-buy__beam'), { rotation: 8, duration: 0.55, ease: 'power2.inOut' })
		.to(q(svg, '[data-bento-layer="custom"]'), { y: -4, scale: 1.03, transformOrigin: 'center', duration: 0.35, ease: 'power2.out' }, '<')
		.to(q(svg, '[data-bento-layer="app"]'), { y: 4, opacity: 0.55, duration: 0.35 }, '<')
		.to({}, { duration: 0.7 })
		.to(q(svg, '.bento-buy__beam'), { rotation: 0, duration: 0.45, ease: 'power2.inOut' })
		.to(q(svg, '[data-bento-layer="custom"]'), { y: 0, scale: 1, duration: 0.35 }, '<')
		.to(q(svg, '[data-bento-layer="app"]'), { y: 0, opacity: 1, duration: 0.35 }, '<');

	return tl;
}

const BUILDERS = {
	conversion: buildConversionTimeline,
	theme: buildThemeTimeline,
	apps: buildAppsTimeline,
	data: buildDataTimeline,
	integration: buildIntegrationTimeline,
	checkout: buildCheckoutTimeline,
	performance: buildPerformanceTimeline,
	admin: buildAdminTimeline,
	code: buildCodeTimeline,
	functions: buildFunctionsTimeline,
	migration: buildMigrationTimeline,
	seo: buildSeoTimeline,
	ops: buildOpsTimeline,
	buy: buildBuyTimeline,
};

class ServiceDetailBento extends HTMLElement {
	connectedCallback() {
		this._instances = [];

		requestAnimationFrame(() => {
			initAnimationsForElement(this);
			this.initializeSvgScenes();
		});
	}

	disconnectedCallback() {
		this._instances.forEach(({ timeline, trigger }) => {
			timeline?.kill();
			trigger?.kill();
		});
		this._instances = [];
		cleanupAnimationsForElement(this);
	}

	initializeSvgScenes() {
		const tiles = this.querySelectorAll('.service-bento__item');
		if (!tiles.length) return;

		if (prefersReducedMotion()) {
			tiles.forEach((tile) => tile.classList.add('is-svg-active'));
			return;
		}

		tiles.forEach((tile) => {
			const svg = q(tile, '[data-bento-animation]');
			if (!svg) return;

			const type = svg.dataset.bentoAnimation;
			const builder = BUILDERS[type];
			if (!builder) return;

			const instance = playWhenVisible(tile, builder);
			if (instance) this._instances.push(instance);
		});
	}
}

if (!customElements.get('service-detail-bento')) {
	customElements.define('service-detail-bento', ServiceDetailBento);
}
